import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, forwardRef, inject, Input, OnDestroy, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { filter, Subscription } from 'rxjs';
import { MagicContainerStore } from '@bn/app-mc/src/lib/stores';
import { Protocol } from '@bn/mc-api/v3';
import { ObjectValidator } from '@bn/shared/entities/validators';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CheckboxComponent } from '@bn/web-ui/checkbox/checkbox.component';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { FormcontrolErrorsComponent } from '@bn/web-ui/standalone/formcontrol-errors/formcontrol-errors.component';

export interface PortMapping {
  containerPort: number;
  exposedPort: number;
  tcp: boolean;
  udp: boolean;
  protocols: Protocol[];
}

interface FormError {
  control: string;
  error: string;
  value: { message: string; refValues: (number | string)[] }; // this looks like it is from RxwebValidators
  currentValue?: number | null;
}

interface CustomPortError {
  control: string;
  message: string;
}

@Component({
  selector: 'bn-dashboard-multiple-ports',
  templateUrl: './multiple-ports.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FaIconComponent,
    InputComponent,
    CheckboxComponent,
    ButtonComponent,
    FormcontrolErrorsComponent,
    MarkAllTouchedDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MultiplePortsComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => MultiplePortsComponent),
    },
  ],
})
export class MultiplePortsComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  readonly #magicContainerStore = inject(MagicContainerStore);

  readonly portInputs = viewChildren<ElementRef<HTMLElement>>('portInput');

  @Input()
  public isInternalIp = false;
  public form = new UntypedFormGroup({
    portMappings: new FormArray([]),
  });
  public selectedPort = -1;
  public allErrors: FormError[] = [];
  public errorsOneObject: number[] = [];
  public errorsMin: string[] = [];
  public errorsMax: string[] = [];
  public errorsRequired: string[] = [];
  public errorsUnique: string[] = [];
  public customPortErrors: CustomPortError[] = [];
  private portSubscriptions = new Subscription();

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.setErrors();
      this.onChange(this.form.value.portMappings);
    });
  }

  @Input()
  public set portId(portId: number) {
    this.selectedPort = portId;
  }

  public get portMappings(): FormArray {
    return this.form.get('portMappings') as FormArray;
  }

  public onChange: (value: PortMapping[]) => void = () => ({});

  public onTouched: () => void = () => ({});

  ngOnDestroy(): void {
    this.portSubscriptions.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.setFocusOnPort(this.selectedPort);
  }

  public removePort(index: number) {
    if (this.portMappings.length === 1) return;

    const ports = this.form.get('portMappings') as FormArray;
    ports.removeAt(index);

    this.setErrors();
  }

  public addPort() {
    if (!this.form.valid) return;

    this.portMappings.push(this.createFormControls());
    this.setFocusOnPort(this.portMappings.length - 1);

    if (this.portMappings.length < 2)
      this.portMappings.patchValue([
        {
          containerPort: 80,
          exposedPort: 80,
          tcp: true,
          udp: true,
        },
      ]);

    this.portMappings.updateValueAndValidity();
    this.form.updateValueAndValidity();

    setTimeout(() => {
      this.onChange(this.portMappings.value);
    });
  }

  public writeValue(data: PortMapping[]) {
    this.portMappings.clear();

    if (!data || data.length === 0) {
      this.addPort();
      this.portMappings.updateValueAndValidity({ emitEvent: true });
    } else {
      data.forEach(() => {
        this.portMappings.push(this.createFormControls());
      });

      this.form.patchValue({ portMappings: data }, { emitEvent: false }); // Prevent triggering valueChanges immediately
    }
  }

  public registerOnChange(fn: (value: PortMapping[]) => void) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  public validate(): ValidationErrors | null {
    return this.form.valid && this.portMappings.valid
      ? null
      : {
          invalidForm: { valid: false, message: 'Port configuration is not valid' },
        };
  }

  public getFormValidationErrors(form: FormGroup) {
    form.updateValueAndValidity({ emitEvent: false });
    const result: FormError[] = [];
    Object.keys(form.controls).forEach((key) => {
      const controlErrors: ValidationErrors = form.get(key)?.errors as ValidationErrors;

      if (controlErrors) {
        Object.keys(controlErrors).forEach((keyError) => {
          result.push({
            control: key,
            error: keyError,
            value: controlErrors[keyError],
            currentValue: form.get(key)?.value as number | null,
          });
        });
      }
    });
    return result;
  }

  public setFocusOnPort(index: number): void {
    setTimeout(() => {
      const inputElement = this.portInputs()[index]?.nativeElement;
      if (inputElement) inputElement.focus();
    });
  }

  private createFormControls() {
    const newForm = new FormGroup(
      {
        containerPort: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(1),
          Validators.max(65535),
          RxwebValidators.unique(),
          RxwebValidators.noneOf({ matchValues: this.#magicContainerStore.forbiddenPorts(), message: 'This port is not allowed and cannot be used.' }),
        ]),
        // Note: in case of public Id we don't have exposed port so just add undefined control without validators
        exposedPort: new FormControl<number | null>(
          null,
          this.isInternalIp
            ? []
            : [
                Validators.required,
                Validators.min(1),
                Validators.max(65535),
                RxwebValidators.unique(),
                RxwebValidators.noneOf({ matchValues: this.#magicContainerStore.forbiddenPorts(), message: 'This port is not allowed and cannot be used.' }),
              ],
        ),
        tcp: new FormControl(true),
        udp: new FormControl(true),
      },
      [ObjectValidator.atLeastOneSelected],
    );

    // Copy value to other field if it is pristine
    this.portSubscriptions.add(
      newForm
        .get('containerPort')
        ?.valueChanges.pipe(filter(() => newForm.get('exposedPort')?.pristine || false))
        .subscribe((value) => {
          const exposedPort = newForm.get('exposedPort');

          if (exposedPort) {
            exposedPort.setValue(value);
            exposedPort.setErrors(null);
            exposedPort.updateValueAndValidity();
          }
        }),
    );

    this.portMappings.updateValueAndValidity();

    return newForm;
  }

  public setErrors(): void {
    this.allErrors = this.getFormValidationErrors(this.form.get('portMappings') as FormGroup);

    // Reset errors
    this.errorsOneObject = [];
    this.errorsMin = [];
    this.errorsMax = [];
    this.errorsRequired = [];
    this.errorsUnique = [];
    this.customPortErrors = [];

    this.portMappings.controls.forEach((control) => {
      if (control.touched) this.allErrors.push(...this.getFormValidationErrors(control as FormGroup));
    });

    this.errorsOneObject = this.allErrors.filter((item) => item.error === 'atLeastOneOptionRequired').map((item) => Number(item.control)) ?? [];
    this.errorsMin = this.allErrors.filter((item) => item.error === 'min').map((item) => item.control) ?? [];
    this.errorsMax = this.allErrors.filter((item) => item.error === 'max').map((item) => item.control) ?? [];
    this.errorsRequired = this.allErrors.filter((item) => item.error === 'required').map((item) => item.control);
    this.errorsUnique = this.allErrors.filter((item) => item.error === 'unique').map((item) => item.control);
    this.customPortErrors = this.allErrors
      .filter((item) => item.error === 'exposedPort' || item.error === 'noneOf')
      .map((item) => ({
        control: item.control,
        message: item.error === 'noneOf' ? this.getPortErrorMessage(item.currentValue) : (item.value?.message ?? this.getPortErrorMessage(item.currentValue)),
      }));

    // If there are errors, mark portMappings as invalid
    this.portMappings.setErrors(this.allErrors.length > 0 ? { invalidPorts: true } : null, { emitEvent: false });
  }

  private getPortErrorMessage(port?: number | null): string {
    return port !== undefined && port !== null ? `Port ${port} is not allowed and cannot be used.` : 'This port is not allowed and cannot be used.';
  }
}
